// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import {
  ACTIVITY_FILTER_DEFAULTS,
  type ActivityEntry,
  type ActivityFilterState,
  ActivityTimeline,
} from "@/components/shared/activity-timeline";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";

function generateMockData(): ActivityEntry[] {
  const now = Date.now();
  const day = 86_400_000;
  return [
    {
      id: 1,
      timestamp: new Date(now - 0.3 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Impact Settings",
      field: "minWorkable",
      oldValue: "5",
      newValue: "3",
    },
    {
      id: 2,
      timestamp: new Date(now - 0.5 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Hours",
      field: "closeTime",
      oldValue: "22:00",
      newValue: "23:00",
    },
    {
      id: 3,
      timestamp: new Date(now - 1.2 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "WiFi Settings",
      field: "ssid",
      oldValue: "StoreWiFi",
      newValue: "StoreWiFi-5G",
    },
    {
      id: 4,
      timestamp: new Date(now - 2 * day).toISOString(),
      user: "admin@focal.systems",
      action: "activated",
      target: "Store",
    },
    {
      id: 5,
      timestamp: new Date(now - 3.5 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Schedule Settings",
      field: "type",
      oldValue: "every_hour",
      newValue: "static",
    },
    {
      id: 6,
      timestamp: new Date(now - 5 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Camera Settings",
      field: "otaVersion",
      oldValue: "2.1.0",
      newValue: "2.2.0",
    },
    {
      id: 7,
      timestamp: new Date(now - 7 * day).toISOString(),
      user: "admin@focal.systems",
      action: "restored",
      target: "Setting Override",
      field: "enable_promo_scoring",
    },
    {
      id: 8,
      timestamp: new Date(now - 10 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Impact Settings",
      field: "sortingLogic",
      oldValue: "name",
      newValue: "weighted_usd",
      message:
        "Changed sorting logic to weighted USD to better reflect revenue impact. This aligns with the new chain-wide strategy.",
    },
    {
      id: 9,
      timestamp: new Date(now - 12 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Match Settings",
      field: "cooldownHours",
      oldValue: "24",
      newValue: "12",
    },
    {
      id: 10,
      timestamp: new Date(now - 15 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Store Details",
      field: "legibleName",
      oldValue: "Store #42",
      newValue: "Downtown Store 42",
    },
    {
      id: 11,
      timestamp: new Date(now - 20 * day).toISOString(),
      user: "admin@focal.systems",
      action: "deactivated",
      target: "Store",
      message: "Store temporarily deactivated for renovation. Expected reactivation in 2 weeks.",
    },
    {
      id: 12,
      timestamp: new Date(now - 25 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Address",
      field: "city",
      oldValue: "San Jose",
      newValue: "San Francisco",
    },
    {
      id: 13,
      timestamp: new Date(now - 35 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Hours",
      field: "openTime",
      oldValue: "08:00",
      newValue: "07:00",
    },
    {
      id: 14,
      timestamp: new Date(now - 50 * day).toISOString(),
      user: "ops@focal.systems",
      action: "deleted",
      target: "Setting Override",
      field: "max_inventory_age",
    },
    {
      id: 15,
      timestamp: new Date(now - 60 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "WiFi Settings",
      field: "password",
      oldValue: "••••••",
      newValue: "••••••",
    },
    {
      id: 16,
      timestamp: new Date(now - 75 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Camera Settings",
      field: "jpgQuality",
      oldValue: "80",
      newValue: "90",
    },
    {
      id: 17,
      timestamp: new Date(now - 85 * day).toISOString(),
      user: "admin@focal.systems",
      action: "created",
      target: "Store",
    },
  ];
}

const MOCK_DATA = generateMockData();

interface StoreActivityTabProps {
  storeName: string;
  initialFilters?: string;
}

export function StoreActivityTab({ storeName, initialFilters }: StoreActivityTabProps) {
  const navigate = useNavigate();
  const initial = useMemo(() => decodeFilters(initialFilters, ACTIVITY_FILTER_DEFAULTS), [initialFilters]);

  const handleFilterChange = useCallback(
    (values: ActivityFilterState) => {
      const encoded = encodeFilters(values, ACTIVITY_FILTER_DEFAULTS);
      navigate({
        to: "/stores/$storeName",
        params: { storeName },
        search: { t: "activity", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, storeName],
  );

  return <ActivityTimeline entries={MOCK_DATA} initialFilters={initial} onFilterChange={handleFilterChange} />;
}
